import { useEffect, useMemo, useState } from 'react'
import {
  Box, Button, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, MenuItem, Select,
  Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined'
import LinkIcon from '@mui/icons-material/Link'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import AddIcon from '@mui/icons-material/Add'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { motion } from 'framer-motion'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { Asset, ChecklistTemplateSummary, Location as Loc, LocationTreeNode } from '@cmms/types'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import { useNavigate } from 'react-router-dom'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'

import { api } from '../../../lib/api'
import { toast } from '../../../store/snackbarStore'
import { useConfirm } from '../../../components/ConfirmDialog'
import { useCopyLink } from '../../../hooks/useCopyLink'
import { useOrgSettings } from '../../../hooks/useOrgSettings'
import { usePermissions } from '../../../hooks/usePermissions'
import { AttachmentSection } from '../../../components/AttachmentSection'
import { QrCodeDialog, qrPayload } from '../../../components/QrCodeDialog'
import { useAuthStore } from '../../../store/authStore'
import { TagsField } from '../../../components/TagsField'
import { useCategoryView } from './categories'
import { SearchableSelect } from '../../../components/SearchableSelect'
import { DateRangeFilterPills, useDateRangeFilter, WorkOrderTimelineList, type WorkOrderTimelineItem } from '../../../components/WorkOrderTimeline'

interface Props {
  location: Loc
  siteId: string
  tree: LocationTreeNode[]
  onSelect: (id: string) => void
}

export function LocationDetail({ location, siteId, tree, onSelect }: Props) {
  const qc = useQueryClient()
  // Auth slug is used to encode the public /report/<slug>/... URL in
  // the QR sticker. Falls back to the internal /locations/... URL
  // when unknown (e.g., super-admin browsing without an active org
  // context — shouldn't normally happen on this screen).
  const orgSlug = useAuthStore((s) => s.user?.org_slug ?? null)
  const navigate = useNavigate()
  const confirmDlg = useConfirm()
  const copyLink = useCopyLink()
  const { formatDate } = useOrgSettings()
  const { can } = usePermissions()
  const cat = useCategoryView(location.type)
  const Icon = cat.Icon
  const [editOpen, setEditOpen] = useState(false)
  const [qrOpen, setQrOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)

  const assetsQ = useQuery({
    queryKey: ['assets', { location_id: location.id }],
    queryFn: () => api.assets.list({ location_id: location.id }),
    staleTime: 30_000,
  })
  const linkedAssets: Asset[] = Array.isArray(assetsQ.data) ? assetsQ.data : []

  const children = useMemo(() => {
    function find(nodes: LocationTreeNode[]): LocationTreeNode[] {
      for (const n of nodes) {
        if (n.id === location.id) return n.children ?? []
        const r = find(n.children ?? [])
        if (r.length > 0) return r
      }
      return []
    }
    return find(tree)
  }, [tree, location.id])

  const deleteM = useMutation({
    mutationFn: () => api.locations.remove(siteId, location.id),
    onSuccess: () => { toast.success('Location deleted'); qc.invalidateQueries({ queryKey: ['location-tree', siteId] }) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to delete'),
  })

  return (
    <motion.div key={location.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.26 }}
      style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>
      <Box sx={{ px: 4, pt: 3.5, pb: 2.5, flexShrink: 0 }}>
        {location.parent_name && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.25 }}>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', cursor: 'pointer', '&:hover': { color: 'text.primary' } }}
              onClick={() => location.parent_id && onSelect(location.parent_id)}>
              {location.parent_name}
            </Typography>
            <ChevronRightIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{location.name}</Typography>
          </Stack>
        )}
        <Stack direction="row" alignItems="center" spacing={1.75}>
          <Box sx={{ width: 42, height: 42, borderRadius: 1.5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: cat.color.bg, color: cat.color.fg, flexShrink: 0 }}>
            <Icon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 }} noWrap>{location.name}</Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.4 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: cat.color.fg }}>{cat.label}</Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>·</Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{location.site_name}</Typography>
            </Stack>
          </Box>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
            <Tooltip title="Copy link" arrow>
              <IconButton size="small" onClick={() => copyLink(`/locations/${location.id}`)}
                sx={{ color: 'primary.main', border: '1px solid', borderColor: 'divider', borderRadius: 1.25, width: 34, height: 34 }}>
                <LinkIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Show QR code" arrow>
              <IconButton size="small" onClick={() => setQrOpen(true)}
                sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider',
                  borderRadius: 1.25, width: 34, height: 34,
                  '&:hover': { color: 'primary.main' } }}>
                <QrCode2OutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={can('locations:update') ? '' : "You don't have permission"} arrow>
              <Box component="span" sx={{ display: 'inline-flex' }}><Button variant="outlined" size="small" startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
                onClick={() => setEditOpen(true)} disabled={!can('locations:update')}
                sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main', borderColor: 'divider', height: 34 }}>Edit</Button></Box>
            </Tooltip>
            <Tooltip title={!can('locations:delete') ? "You don't have permission" : children.length > 0 ? 'Remove sub-locations first' : 'Delete'} arrow>
              <span>
                <IconButton size="small" disabled={children.length > 0 || !can('locations:delete')}
                  onClick={async () => {
                    const ok = await confirmDlg({ title: `Delete ${location.name}?`, message: 'This location will be permanently removed. Any assets linked to it will be unlinked.', confirmLabel: 'Delete', variant: 'danger' })
                    if (ok) deleteM.mutate()
                  }}
                  sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25, width: 34, height: 34, '&:hover': { color: 'error.main' } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
      <Divider />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 4, py: 3,
        scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
        '&::-webkit-scrollbar': { width: 8 }, '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
        '&:hover::-webkit-scrollbar-thumb': { background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)' } }}>

        {location.description && (<><Sec title="Description"><Typography sx={{ fontSize: 13.5, lineHeight: 1.65 }}>{location.description}</Typography></Sec><Divider /></>)}

        {/* Tag assignment — autosaves via PATCH /locations/:id
            with `tag_ids` on every add / remove. */}
        <Sec title="Tags">
          <Box sx={{ py: 0.5 }}>
            <TagsField
              value={location.tags ?? []}
              onChange={(next) =>
                api.locations.update(siteId, location.id, {
                  tag_ids: next.map((t) => t.id),
                }).then(() => qc.invalidateQueries({ queryKey: ['location-tree', siteId] }))
                  .catch((e) => toast.error(e instanceof ApiError ? e.message : 'Failed to save tags'))
              }
              helperText="Group locations by tag on the detailed overview."
            />
          </Box>
        </Sec>
        <Divider />

        <Sec title={`Sub-locations (${children.length})`}>
          {children.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1.5 }}>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 1.5 }}>No sub-locations yet.</Typography>
              {can('locations:create') && (
                <Button size="small" variant="outlined" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                  onClick={() => setCreateOpen(true)}
                  sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main', borderColor: 'divider' }}>Add sub-location</Button>
              )}
            </Box>
          ) : (
            <Stack spacing={0.75}>
              {children.map((c) => <ChildRow key={c.id} node={c} onSelect={onSelect} />)}
              {can('locations:create') && (
                <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={() => setCreateOpen(true)}
                  sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main', mt: 1 }}>Add sub-location</Button>
              )}
            </Stack>
          )}
        </Sec>
        <Divider />

        <Sec title="Details">
          <Stack spacing={1.25}>
            <Kv label="Type" value={cat.label} />
            <Kv label="Site" value={location.site_name} />
            {location.parent_name && <Kv label="Parent" value={`${location.parent_name} (${location.parent_type ?? ''})`} />}
            <Kv label="Created" value={formatDate(location.created_at)} />
          </Stack>
        </Sec>
        <Divider />

        <Sec title={`Assets at this location (${linkedAssets.length})`}>
          {linkedAssets.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1.5 }}>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>No assets at this location.</Typography>
            </Box>
          ) : (
            <Stack spacing={0}>
              {linkedAssets.map((a) => (
                <Stack key={a.id} direction="row" alignItems="center" spacing={1.5}
                  onClick={() => navigate(`/assets/${a.id}`)}
                  sx={{
                    px: 1.25, py: 1.25, borderRadius: 1.25, cursor: 'pointer',
                    transition: 'background-color 0.15s',
                    '&:hover': { bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)' },
                  }}>
                  <Box sx={{
                    width: 30, height: 30, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    bgcolor: a.category_color ? `${a.category_color}18` : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)',
                    color: a.category_color ?? 'text.secondary',
                  }}>
                    <HubOutlinedIcon sx={{ fontSize: 15 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{a.name}</Typography>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography sx={{ fontSize: 10.5, fontFamily: 'ui-monospace, monospace', color: 'text.secondary' }}>{a.asset_code}</Typography>
                      {a.category_name && (
                        <>
                          <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>·</Typography>
                          <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>{a.category_name}</Typography>
                        </>
                      )}
                    </Stack>
                  </Box>
                  <OpenInNewOutlinedIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                </Stack>
              ))}
            </Stack>
          )}
        </Sec>
      </Box>

      {/* Attachments */}
      <Box sx={{ px: 4, py: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <AttachmentSection
          queryKey={['location-attachments', location.id]}
          listFn={() => api.locations.listAttachments(location.id)}
          uploadFn={(file, onProgress) => api.locations.uploadAttachment(location.id, file, onProgress)}
          viewFn={(doc) => api.locations.viewAttachment(location.id, doc.id)}
          deleteFn={(docId) => api.locations.deleteAttachment(location.id, docId)}
          canEdit={can('locations:update')}
          maxFiles={10}
          title="Photos & documents"
        />
      </Box>

      {/* Linked checklists */}
      <LocationChecklistsSection locationId={location.id} />

      {/* Linked work orders / tasks */}
      <LocationWorkOrdersSection locationId={location.id} />

      <Box sx={{ flexShrink: 0, px: 4, py: 2.25, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'center', gap: 1 }}>
        {(can('tasks:create') || can('work_orders:create')) && (
          <>
            {/* Task variant — soft-services. Lives in the Tasks module
                now, so the trigger points at /tasks; the Tasks page
                forces kind=task on the create dialog server-side. */}
            {can('tasks:create') && (
              <Button variant="outlined"
                onClick={() => navigate(`/tasks?new=1&location_id=${location.id}&site_id=${location.site_id ?? ''}`)}
                sx={{ fontSize: 13, fontWeight: 600, color: '#1D4ED8', borderColor: '#1D4ED8', borderRadius: 99, px: 2.5, py: 0.75 }}>
                Use in new task
              </Button>
            )}
            {can('work_orders:create') && (
              <Button variant="outlined" startIcon={<BuildOutlinedIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate(`/work-orders?new=1&location_id=${location.id}&site_id=${location.site_id ?? ''}`)}
                sx={{ fontSize: 13, fontWeight: 600, color: 'primary.main', borderColor: 'primary.main', borderRadius: 99, px: 2.5, py: 0.75 }}>
                Use in new work order
              </Button>
            )}
          </>
        )}
      </Box>

      <EditDialog open={editOpen} onClose={() => setEditOpen(false)} location={location} siteId={siteId} />
      <CreateDialog open={createOpen} onClose={() => setCreateOpen(false)} siteId={siteId} parentId={location.id} parentType={location.type} />
      <QrCodeDialog open={qrOpen} onClose={() => setQrOpen(false)}
        payload={
          // SOS location QR — encodes the public "Report an issue"
          // URL. When scanned by a signed-in user, the page redirects
          // to the internal location detail. When scanned by an
          // anonymous visitor, they see the intake form.
          orgSlug
            ? qrPayload.locationReport(orgSlug, siteId, location.id)
            : qrPayload.location(siteId, location.id)
        }
        title={location.name}
        kind="Location" />
    </motion.div>
  )
}

function ChildRow({ node, onSelect }: { node: LocationTreeNode; onSelect: (id: string) => void }) {
  const cat = useCategoryView(node.type)
  const CIcon = cat.Icon
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} onClick={() => onSelect(node.id)}
      sx={{ px: 1.25, py: 1, borderRadius: 1.25, cursor: 'pointer', '&:hover': { bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)' } }}>
      <Box sx={{ width: 28, height: 28, borderRadius: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', bgcolor: cat.color.bg, color: cat.color.fg, flexShrink: 0 }}>
        <CIcon sx={{ fontSize: 15 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{node.name}</Typography>
        <Typography sx={{ fontSize: 11, color: cat.color.fg, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, opacity: 0.85 }}>{cat.label}</Typography>
      </Box>
      {(node.children ?? []).length > 0 && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{(node.children ?? []).length} sub</Typography>}
      <ChevronRightIcon sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.5, flexShrink: 0 }} />
    </Stack>
  )
}

// ─── Dialogs ────────────────────────────────────────────────────────────────

const LOC_TYPES = [
  { value: 'building', label: 'Building' }, { value: 'floor', label: 'Floor' },
  { value: 'wing', label: 'Wing' }, { value: 'room', label: 'Room' },
  { value: 'area', label: 'Area' }, { value: 'zone', label: 'Zone' },
  { value: 'outdoor', label: 'Outdoor' }, { value: 'other', label: 'Other' },
]

function EditDialog({ open, onClose, location, siteId }: { open: boolean; onClose: () => void; location: Loc; siteId: string }) {
  const qc = useQueryClient()
  const [name, setName] = useState(location.name)
  const [desc, setDesc] = useState(location.description ?? '')
  const [clIds, setClIds] = useState<string[]>([])
  const [defaultAssigneeId, setDefaultAssigneeId] = useState<string>((location as any).default_assignee_user_id ?? '')

  const existingClQ = useQuery({ queryKey: ['location-checklists', location.id], queryFn: () => api.locations.listChecklists(location.id), enabled: open })
  useEffect(() => { if (existingClQ.data) setClIds(existingClQ.data.map(c => c.template_id)) }, [existingClQ.data])

  const allClQ = useQuery({ queryKey: ['checklists', { status: 'published' }], queryFn: () => api.checklists.list({ status: 'published' }), staleTime: 60_000, enabled: open })
  const allCl = Array.isArray(allClQ.data) ? allClQ.data : []
  const usersQ = useQuery({ queryKey: ['users'], queryFn: () => api.users.list(), staleTime: 5 * 60_000, enabled: open })
  const users: any[] = Array.isArray(usersQ.data) ? usersQ.data : []

  const m = useMutation({
    mutationFn: async () => {
      await api.locations.update(siteId, location.id, {
        name: name.trim(),
        description: desc.trim() || null,
        // Null clears; '' is treated as "clear" too — the schema
        // accepts both string uuid and null.
        default_assignee_user_id: defaultAssigneeId ? defaultAssigneeId : null,
      })
      const existing = (existingClQ.data ?? []).map(c => c.template_id)
      for (const tid of existing) { if (!clIds.includes(tid)) await api.locations.removeChecklist(location.id, tid).catch(() => {}) }
      for (const tid of clIds) { if (!existing.includes(tid)) await api.locations.addChecklist(location.id, tid).catch(() => {}) }
    },
    onSuccess: () => { toast.success('Updated'); qc.invalidateQueries({ queryKey: ['location-tree', siteId] }); qc.invalidateQueries({ queryKey: ['location-checklists', location.id] }); onClose() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Edit {location.name}</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>Type ({location.type}) is immutable.</Typography>
        <Stack spacing={2}>
          <TextField label="Name" required value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth autoFocus />
          <TextField label="Description" value={desc} onChange={(e) => setDesc(e.target.value)} size="small" fullWidth multiline minRows={2} />
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>Default assignee</Typography>
            <select
              value={defaultAssigneeId}
              onChange={(e) => setDefaultAssigneeId(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.23)', fontSize: 14, background: 'transparent', color: 'inherit' }}
            >
              <option value="">— None —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
            <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.5 }}>
              Workflows can auto-assign to this person using the "Location owner" token.
            </Typography>
          </Box>
          <ChecklistMultiSelect ids={clIds} onChange={setClIds} checklists={allCl} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" onClick={() => m.mutate()} disabled={!name.trim() || m.isPending}>{m.isPending ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  )
}

export function CreateDialog({ open, onClose, siteId, parentId, parentType }: { open: boolean; onClose: () => void; siteId: string; parentId?: string; parentType?: string }) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [type, setType] = useState('area')
  const [desc, setDesc] = useState('')
  const [clIds, setClIds] = useState<string[]>([])

  const allClQ = useQuery({ queryKey: ['checklists', { status: 'published' }], queryFn: () => api.checklists.list({ status: 'published' }), staleTime: 60_000, enabled: open })
  const allCl = Array.isArray(allClQ.data) ? allClQ.data : []

  const m = useMutation({
    mutationFn: async () => {
      const loc = await api.locations.create(siteId, { name: name.trim(), type: type as any, parent_id: parentId, description: desc.trim() || undefined })
      for (const tid of clIds) { await api.locations.addChecklist(loc.id, tid).catch(() => {}) }
      return loc
    },
    onSuccess: () => { toast.success('Created'); qc.invalidateQueries({ queryKey: ['location-tree', siteId] }); setName(''); setType('area'); setDesc(''); setClIds([]); onClose() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>{parentId ? 'Add sub-location' : 'New location'}</DialogTitle>
      <DialogContent>
        {parentType && <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>Adding inside a {parentType}.</Typography>}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name" required value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth autoFocus />
          <Select value={type} onChange={(e) => setType(e.target.value)} size="small" fullWidth>{LOC_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}</Select>
          <TextField label="Description (optional)" value={desc} onChange={(e) => setDesc(e.target.value)} size="small" fullWidth multiline minRows={2} />
          <ChecklistMultiSelect ids={clIds} onChange={setClIds} checklists={allCl} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" onClick={() => m.mutate()} disabled={!name.trim() || m.isPending}>{m.isPending ? 'Creating…' : 'Create'}</Button>
      </DialogActions>
    </Dialog>
  )
}

function Sec({ title, children }: { title: string; children: React.ReactNode }) {
  return <Box sx={{ py: 2.5 }}><Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1.25 }}>{title}</Typography>{children}</Box>
}
function Kv({ label, value }: { label: string; value: string }) {
  return <Stack direction="row" alignItems="center" spacing={1.5}><Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', minWidth: 80 }}>{label}</Typography><Typography sx={{ fontSize: 13, fontWeight: 500 }}>{value}</Typography></Stack>
}

function ChecklistMultiSelect({ ids, onChange, checklists }: { ids: string[]; onChange: (ids: string[]) => void; checklists: ChecklistTemplateSummary[] }) {
  return (
    <>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
        Linked checklists
      </Typography>
      <SearchableSelect
        multiple
        value={ids}
        onChange={onChange}
        options={checklists}
        getValue={(c) => c.id}
        getLabel={(c) => c.name}
        placeholder="Select checklists"
        renderOption={(c) => (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
            <ChecklistOutlinedIcon sx={{ fontSize: 16, color: ids.includes(c.id) ? '#8B5CF6' : 'text.secondary' }} />
            <Typography sx={{ fontSize: 13, flex: 1 }}>{c.name}</Typography>
            {ids.includes(c.id) && <CheckCircleOutlinedIcon sx={{ fontSize: 16, color: '#059669' }} />}
          </Stack>
        )}
      />
    </>
  )
}

function LocationWorkOrdersSection({ locationId }: { locationId: string }) {
  const navigate = useNavigate()
  const { formatDate } = useOrgSettings()
  // kind: 'all' — unlike assets/vendors, a task CAN be scoped to a
  // location (only asset/vendor/parts payloads are rejected for
  // tasks), so the default work_order-only filter would silently hide
  // a location's soft-services task history.
  const woQ = useQuery({
    queryKey: ['location-work-orders', locationId],
    queryFn: () => api.workOrders.list({ location_id: locationId, kind: 'all' }),
    staleTime: 30_000,
  })
  const workOrders = Array.isArray(woQ.data) ? woQ.data : []
  const sorted = [...workOrders].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  const { range, setRange, filtered } = useDateRangeFilter(sorted, (wo) => wo.created_at)

  const items: WorkOrderTimelineItem[] = filtered.map((wo) => ({
    id: wo.id,
    title: wo.title,
    code: wo.wo_code,
    status: wo.status,
    priority: wo.priority,
    isTask: wo.kind === 'task',
    assignee: wo.assigned_to_user_name ?? wo.assigned_to_team_name ?? null,
    createdAt: wo.created_at,
    finishedAt: wo.closed_at ?? wo.resolved_at ?? null,
    onClick: () => navigate(wo.kind === 'task' ? `/tasks/${wo.id}` : `/work-orders/${wo.id}`),
  }))

  return (
    <Box sx={{ px: 4, py: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1.25 }}>Work orders & tasks</Typography>
      <DateRangeFilterPills range={range} onChange={setRange} />
      <WorkOrderTimelineList
        items={items}
        formatDate={formatDate}
        loading={woQ.isLoading}
        emptyMessage={sorted.length === 0 ? 'No work orders or tasks at this location yet.' : 'No work orders or tasks in this date range.'}
      />
    </Box>
  )
}

function LocationChecklistsSection({ locationId }: { locationId: string }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const [addOpen, setAddOpen] = useState(false)
  const [selectedId, setSelectedId] = useState('')

  const clQ = useQuery({ queryKey: ['location-checklists', locationId], queryFn: () => api.locations.listChecklists(locationId) })
  const linked = Array.isArray(clQ.data) ? clQ.data : []

  const allQ = useQuery({ queryKey: ['checklists', { status: 'published' }], queryFn: () => api.checklists.list({ status: 'published' }), staleTime: 60_000 })
  const allChecklists = Array.isArray(allQ.data) ? allQ.data : []

  const addM = useMutation({
    mutationFn: (templateId: string) => api.locations.addChecklist(locationId, templateId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['location-checklists', locationId] }); toast.success('Checklist linked'); setAddOpen(false); setSelectedId('') },
    onError: (e: any) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const removeM = useMutation({
    mutationFn: (templateId: string) => api.locations.removeChecklist(locationId, templateId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['location-checklists', locationId] }); toast.success('Checklist unlinked') },
    onError: (e: any) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  return (
    <Box sx={{ px: 4, py: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Linked checklists</Typography>
        {can('locations:update') && (
          <Button size="small" startIcon={<AddCircleOutlineIcon sx={{ fontSize: 14 }} />} onClick={() => setAddOpen(true)}
            sx={{ fontSize: 11.5, fontWeight: 600, color: 'primary.main' }}>Add</Button>
        )}
      </Stack>

      {linked.length > 0 ? (
        <Stack spacing={1}>
          {linked.map((cl) => (
            <Stack key={cl.id} direction="row" alignItems="center" spacing={1.5}
              sx={{ px: 2, py: 1, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
                cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
              onClick={() => navigate(`/checklists/${cl.template_id}`)}>
              <ChecklistOutlinedIcon sx={{ fontSize: 16, color: '#8B5CF6' }} />
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, flex: 1 }} noWrap>{cl.name}</Typography>
              {can('locations:update') && (
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeM.mutate(cl.template_id) }}
                  disabled={removeM.isPending}
                  sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Stack>
          ))}
        </Stack>
      ) : (
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>No checklists linked</Typography>
      )}

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Link checklist to location</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <SearchableSelect
              value={selectedId}
              onChange={setSelectedId}
              options={allChecklists.filter((c) => !linked.some((l) => l.template_id === c.id))}
              getValue={(c) => c.id}
              getLabel={(c) => c.name}
              placeholder="Select a checklist"
              renderOption={(c) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ChecklistOutlinedIcon sx={{ fontSize: 16, color: '#8B5CF6' }} />
                  <Typography sx={{ fontSize: 13 }}>{c.name}</Typography>
                </Stack>
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAddOpen(false)} sx={{ color: 'text.secondary' }}>Cancel</Button>
          <Button variant="contained" disabled={!selectedId || addM.isPending} onClick={() => addM.mutate(selectedId)}>
            {addM.isPending ? 'Linking...' : 'Link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
